import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  event_add,
  event_update,
  event_edit,
  event_course_options,
  test_types_for_branch,
} from "../../../api/Events";

// Shared event/test form. Renders just the form body (no page or modal
// chrome) so it can be embedded inside the dedicated page (AddEdit.jsx)
// or a calendar slot-click modal.
//
// Props:
//   editing   — true to load an existing event by `eventId`
//   eventId   — event id when editing
//   initial   — optional defaults (e.g. { date, time_start } from calendar slot)
//   onSaved   — callback after a successful save
//   onCancel  — optional cancel callback
//   submitLabel — text for the submit button (default "Create" / "Save changes")
const EventForm = ({ editing, eventId, initial, onSaved, onCancel, submitLabel }) => {
  const branchId = localStorage.getItem("b_id");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      event_type: initial?.event_type || "event",
      scope: initial?.scope || "branch",
      title: initial?.title || "",
      date: initial?.date || "",
      time_start: initial?.time_start || "",
      grade_id: initial?.grade_id || "",
      course_id: initial?.course_id || "",
      venue_name: initial?.venue_name || "",
      description: initial?.description || "",
      test_type_id: initial?.test_type_id || "",
      duration_minutes: initial?.duration_minutes || "",
    },
  });

  const eventType = watch("event_type");
  const scope = watch("scope");
  const selectedTestTypeId = watch("test_type_id");

  const [grades, setGrades] = useState([]);
  const [courses, setCourses] = useState([]);
  const [testTypes, setTestTypes] = useState([]);

  useEffect(() => {
    if (!branchId) return;
    event_course_options(branchId).then((res) => {
      setGrades(res?.data?.grades || []);
      setCourses(res?.data?.courses || []);
    });
    test_types_for_branch().then((res) => {
      setTestTypes(res?.data || []);
    });
  }, [branchId]);

  const selectedTestType = testTypes.find(
    (t) => String(t.id) === String(selectedTestTypeId)
  );

  // Build a clear human label for a test_type row.
  const labelForTestType = (t) => {
    const ctx = [
      t.year,
      t.grade ? `Y${t.grade}` : null,
      t.term ? `T${t.term}` : null,
      t.subject_name,
      t.kind,
    ]
      .filter(Boolean)
      .join(" · ");
    return ctx ? `${t.test_title} — ${ctx}` : t.test_title;
  };

  useEffect(() => {
    if (!editing || !eventId) return;
    event_edit(eventId).then((res) => {
      const e = res?.data;
      if (!e) return;
      setValue("event_type", e.event_type || "event");
      setValue("title", e.title || "");
      setValue("date", e.date || "");
      setValue("time_start", e.time_start || "");
      setValue("venue_name", e.venue_name || "");
      setValue("description", e.description || "");
      setValue("test_type_id", e.test_type_id ?? "");
      // If duration is missing on an old row, derive it from start + end so
      // the field shows something sensible when editing legacy events.
      let dur = e.duration_minutes;
      if ((dur === null || dur === undefined || dur === "") && e.time_start && e.time_end) {
        const [sh, sm] = String(e.time_start).split(":").map(Number);
        const [eh, em] = String(e.time_end).split(":").map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff > 0) dur = diff;
      }
      setValue("duration_minutes", dur ?? "");
      if (e.course_id) {
        setValue("scope", "course");
        setValue("course_id", e.course_id);
      } else if (e.grade_id) {
        setValue("scope", "grade");
        setValue("grade_id", e.grade_id);
      } else {
        setValue("scope", "branch");
      }
    });
  }, [editing, eventId, setValue]);

  const onSubmit = async (data) => {
    const payload = {
      branch_id: branchId,
      event_type: data.event_type,
      title: data.title,
      date: data.date,
      time_start: data.time_start || null,
      scope: data.scope,
      grade_id: data.grade_id ? Number(data.grade_id) : null,
      course_id: data.course_id ? Number(data.course_id) : null,
      venue_name: data.venue_name || null,
      description: data.description || null,
      test_type_id: data.test_type_id ? Number(data.test_type_id) : null,
      duration_minutes: data.duration_minutes ? Number(data.duration_minutes) : null,
    };
    const res = editing
      ? await event_update(eventId, payload)
      : await event_add(payload);
    if (res?.status && onSaved) onSaved(res);
  };

  return (
    <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
      <div className="row">
        <div className="col-md-12" style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>Type *</label>
          <div style={{ display: "flex", gap: 16 }}>
            <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="radio" value="event" {...register("event_type", { required: true })} /> Event
            </label>
            <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="radio" value="test" {...register("event_type", { required: true })} /> Test
            </label>
          </div>
        </div>

        <div className="col-md-12">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              className="form-control"
              placeholder={eventType === "test" ? "e.g. Y7 Maths Term Test" : "e.g. Parent Information Night"}
              {...register("title", { required: "Required" })}
            />
            {errors.title && <span className="validation-error">{errors.title.message}</span>}
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group">
            <label>Date *</label>
            <input type="date" className="form-control" {...register("date", { required: "Required" })} />
            {errors.date && <span className="validation-error">{errors.date.message}</span>}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Start Time</label>
            <input type="time" className="form-control" {...register("time_start")} />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Duration (minutes)</label>
            <select className="form-select form-control" {...register("duration_minutes", { valueAsNumber: true })}>
              <option value="">Choose duration</option>
              {Array.from({ length: 16 }, (_, i) => (i + 1) * 30).map((v) => {
                const label =
                  v === 30 ? "30 min"
                  : v === 60 ? "1 hour"
                  : `${v / 60} hours`;
                return (
                  <option key={v} value={v}>{`${label} (${v})`}</option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="col-md-12" style={{ marginTop: 12, marginBottom: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>Audience *</label>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="radio" value="branch" {...register("scope", { required: true })} /> Whole branch
            </label>
            <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="radio" value="grade" {...register("scope", { required: true })} /> Specific grade
            </label>
            <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="radio" value="course" {...register("scope", { required: true })} /> Specific course
            </label>
          </div>
        </div>

        {scope === "grade" && (
          <div className="col-md-6">
            <div className="form-group">
              <label>Grade *</label>
              <select className="form-select form-control" {...register("grade_id", { required: scope === "grade" })}>
                <option value="">Choose Grade</option>
                {grades.map((g) => (
                  <option key={g.id} value={g.id}>Year {g.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {scope === "course" && (
          <div className="col-md-6">
            <div className="form-group">
              <label>Course *</label>
              <select className="form-select form-control" {...register("course_id", { required: scope === "course" })}>
                <option value="">Choose Course</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {eventType === "test" && (
          <>
            <div className="col-md-12">
              <div style={{
                fontSize: 12, color: "#6b7280", background: "#f9fafb",
                border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", margin: "8px 0 12px",
              }}>
                <strong style={{ color: "#374151" }}>Choose an assessment</strong> — pick from the list HQ has set up
                in Test Management. The test link is wired up for students automatically.
              </div>
            </div>
            <div className="col-md-12">
              <div className="form-group">
                <label>Assessment *</label>
                <select
                  className="form-select form-control"
                  {...register("test_type_id", { required: eventType === "test" })}
                >
                  <option value="">Choose an assessment…</option>
                  {testTypes.length === 0 ? (
                    <option value="" disabled>No assessments yet — ask HQ to add one</option>
                  ) : (
                    testTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {labelForTestType(t)}
                      </option>
                    ))
                  )}
                </select>
                {selectedTestType?.test_period && (
                  <p style={{ fontSize: 12, color: "#64748b", margin: "6px 0 0" }}>
                    Default test period: {selectedTestType.test_period} min
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        <div className="col-md-6">
          <div className="form-group">
            <label>Venue</label>
            <input type="text" className="form-control" placeholder="Where will this happen?" {...register("venue_name")} />
          </div>
        </div>
        <div className="col-md-12">
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-control" rows="3" {...register("description")}></textarea>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {submitLabel || (editing ? "Save changes" : "Create")}
        </button>
      </div>
    </form>
  );
};

export default EventForm;
